import React, { useCallback, useEffect, useRef, useState } from "react";
import { Virtuoso, VirtuosoHandle } from "react-virtuoso";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { container } from "@/utils/mobx";
import { TextInput } from "@/components/toolkit/TextInput";
import { ConfirmDialog } from "@/components/toolkit/ConfirmDialog";
import { Clickable } from "@/components/toolkit/Clickable";
import { AgentPaneStore } from "./store";
import { resolveWidget } from "./widgets";
import { SessionSettingsModal } from "./c.SessionSettingsModal";
import { PromptEventStore } from "@/app_store/agent/PromptEvent.generated";
import { t } from "@/i18n";
import "./s.agent_pane.css";


const VirtuosoScroller = React.forwardRef<HTMLDivElement, React.HTMLProps<HTMLDivElement>>(
    ({ style, ...props }, ref) => <div {...props} ref={ref} style={{ ...style, overflowX: 'hidden' }} />
);
const VirtuosoHeader = () => <div style={{ height: 10 }} />;
const VirtuosoFooter = () => <div style={{ height: 10 }} />;
const virtuosoComponents = { Scroller: VirtuosoScroller, Header: VirtuosoHeader, Footer: VirtuosoFooter };
// Render extra px of timeline rows above/below the viewport so scrolling
// through history stays smooth instead of flashing blank. Smaller than the
// deployment log buffer since chat items (markdown/widgets) are heavier.
const TIMELINE_OVERSCAN_PX = 600;

export const AgentPane = container((props: {
    store: AgentPaneStore;
}) => {
    const store = props.store;
    const virtuosoRef = useRef<VirtuosoHandle>(null);
    const inputAreaRef = useRef<HTMLDivElement>(null);
    const timelineWrapRef = useRef<HTMLDivElement>(null);
    const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());

    const toggleExpanded = useCallback((index: number) => {
        setExpandedItems(prev => {
            const next = new Set(prev);
            if (next.has(index)) next.delete(index);
            else next.add(index);
            return next;
        });
    }, []);

    // When switching sessions, restore scroll position.
    useEffect(() => {
        if (!store.activeTabId) return;
        const pos = store.position;
        if (pos === null) scrollToBottom();
        else virtuosoRef.current?.scrollToIndex({ index: pos, align: "start" });
    }, [store.activeTabId]);

    // Scroll to bottom when active tab changes and we're pinned to bottom.
    useEffect(() => {
        if (!store.isAtBottom) return;
        scrollToBottom();
    }, [store.activeTabId]);

    // When input area or timeline container resizes, keep scroll pinned to bottom.
    useEffect(() => {
        const els = [inputAreaRef.current, timelineWrapRef.current].filter(Boolean) as HTMLElement[];
        if (!els.length) return;
        const ro = new ResizeObserver(() => {
            if (store.isAtBottom) scrollToBottom();
        });
        els.forEach(el => ro.observe(el));
        return () => ro.disconnect();
    }, []);

    // Focus the prompt input when a new session is created.
    useEffect(() => {
        if (store.sessionCreatedCount === 0) return;
        const el = inputAreaRef.current?.querySelector("textarea");
        el?.focus();
    }, [store.sessionCreatedCount]);

    const items = store.activeSession?.flat_history ?? [];

    const scrollToLastUserPrompt = () => {
        if (store.lastUserItemIndex >= 0) {
            virtuosoRef.current?.scrollToIndex({ index: store.lastUserItemIndex, behavior: "smooth", align: "start" });
        }
    };

    const scrollToBottom = () => {
        store.changePosition(null);
        virtuosoRef.current?.scrollToIndex({ index: "LAST", align: "end" });
    };

    const sessionLabel = (cwd: string | null) => {
        if (!cwd) return "session";
        const parts = cwd.replace(/\/$/, "").split("/");
        return parts[parts.length - 1] || "session";
    };

    const itemCssClass = (type: string) => {
        if (type === "text") return "assistant";
        if (type === "tool_use") return "action";
        return type;
    };

    const renderItem = useCallback((index: number, item: PromptEventStore) => {
        const isExpanded = expandedItems.has(index);

        return (
            <div data-item-index={index} className={`agent_pane__item agent_pane__item--${itemCssClass(item.type)}`}>
                <div className="agent_pane__item_label">{item.type}</div>
                {item.type === "log" ? (
                    <div className="agent_pane__item_content agent_pane__log">
                        <span className="agent_pane__log_source">{item.source}</span>
                        <span className="agent_pane__log_path">{item.path}</span>
                    </div>
                ) : item.type === "text" ? (
                    <div className="agent_pane__item_content agent_pane__item_content--markdown">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{item.content}</ReactMarkdown>
                    </div>
                ) : item.type === "tool_use" ? (() => {
                    const Widget = resolveWidget(item.tool_name);
                    return (
                        <Widget
                            item={item}
                            index={index}
                            isExpanded={isExpanded}
                            toggleExpanded={toggleExpanded}
                            onOpenInCode={(path) => store.openFileInCode(path)}
                            onOpenInReview={(path) => store.openFileInReview(path)}
                        />
                    );
                })() : item.type === "user" ? (
                    <div className="agent_pane__item_content">
                        <pre className="agent_pane__item_pre">{item.content}</pre>
                    </div>
                ) : null}
            </div>
        );
    }, [expandedItems, store]);

    return (
        <div className="agent_pane">
            <div className="agent_pane__tabs">
                {store.allTabs.map(tab => (
                    <Clickable key={tab.id} onClick={() => store.selectClaudeSession(tab.id)}>
                        <div className={`agent_pane__tab${store.activeTabId === tab.id ? " agent_pane__tab--active" : ""}`}>
                            {tab.session?.is_streaming && (
                                <span className="agent_pane__tab_spinner" />
                            )}
                            <span className="agent_pane__tab_label">{sessionLabel((tab.session?.container as any)?.cwd ?? null)}</span>
                            <Clickable stopPropagation onClick={() => store.changeConfirmCloseId(tab.id)}>
                                <button
                                    className="agent_pane__tab_close"
                                    title={t("agent_close_session")}
                                >×</button>
                            </Clickable>
                        </div>
                    </Clickable>
                ))}
                <Clickable onClick={() => store.createClaudeSession()}>
                    <button
                        className="agent_pane__tab_new"
                        disabled={!store.isConnected}
                        title={t("agent_new_session")}
                    >+</button>
                </Clickable>
            </div>

            {store.activeTabId ? (
                <>
                    <div className="agent_pane__timeline_wrap" ref={timelineWrapRef} data-banner-bottom={store.promptBannerPosition === "bottom" || undefined}>
                        {store.promptBannerPosition && store.lastUserItemContent && (
                            <Clickable onClick={scrollToLastUserPrompt}>
                                <div className={`agent_pane__prompt_banner agent_pane__prompt_banner--${store.promptBannerPosition}`}>
                                    {store.lastUserItemContent}
                                </div>
                            </Clickable>
                        )}
                        <Virtuoso
                            ref={virtuosoRef}
                            className="agent_pane__timeline"
                            components={virtuosoComponents}
                            data={items}
                            itemContent={renderItem}
                            increaseViewportBy={TIMELINE_OVERSCAN_PX}
                            followOutput={(isAtBottom) => isAtBottom ? "auto" : false}
                            rangeChanged={(range) => { store.changeRenderedRange(range); }}
                            atBottomStateChange={(atBottom) => { store.changePosition(atBottom ? null : store.renderedRange.startIndex); }}
                        />
                        {store.thinkingDots > 0 && (
                            <div className="agent_pane__thinking">
                                {"Thinking" + ".".repeat(store.thinkingDots)}
                            </div>
                        )}
                        {!store.isAtBottom && (
                            <Clickable onClick={scrollToBottom}>
                                <button className="agent_pane__scroll_btn">↓</button>
                            </Clickable>
                        )}
                    </div>
                    <div className="agent_pane__input_area" ref={inputAreaRef}>
                        {store.contextFilePaths.length > 0 && (
                            <div className="agent_pane__context_chips">
                                {store.contextFilePaths.map(absolutePath => (
                                    <div key={absolutePath} className="agent_pane__context_bar">
                                        <span className="agent_pane__context_icon">@</span>
                                        <span className="agent_pane__context_name">{absolutePath.split("/").filter(Boolean).pop()}</span>
                                        <Clickable onClick={() => store.removeContextFile(absolutePath)}>
                                            <button className="agent_pane__context_dismiss" title={t("agent_context_remove")}>×</button>
                                        </Clickable>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className="agent_pane__input_row">
                            <Clickable onClick={() => store.openSessionSettings(store.activeTabId!)}>
                                <button
                                    className="agent_pane__settings_btn"
                                    title={t("agent_session_settings")}
                                >⚙</button>
                            </Clickable>
                            <Clickable onClick={() => store.toggleActiveFileContext()}>
                                <button
                                    className={`agent_pane__context_btn${store.isActiveFileInContext ? " agent_pane__context_btn--active" : ""}`}
                                    title={t("agent_context_toggle")}
                                >@</button>
                            </Clickable>
                            <Clickable onClick={() => store.toggleReadonlyMode()}>
                                <button
                                    className={`agent_pane__context_btn${store.readonlyMode ? " agent_pane__context_btn--active" : ""}`}
                                    title={t("agent_readonly_toggle")}
                                >⊘</button>
                            </Clickable>
                            <TextInput
                                value={store.prompt}
                                isTextArea
                                submitOnEnter
                                placeholder={t("agent_placeholder")}
                                onChange={(v) => store.changePrompt(v)}
                                onEnter={() => store.sendPrompt()}
                            />
                            {store.isStreaming ? (
                                <Clickable onClick={() => store.interruptClaudeSession()}>
                                    <button className="agent_pane__send_btn agent_pane__send_btn--stop">
                                        {t("agent_stop")}
                                    </button>
                                </Clickable>
                            ) : (
                                <Clickable onClick={() => store.sendPrompt()}>
                                    <button
                                        className="agent_pane__send_btn"
                                        disabled={!store.prompt.trim() || store.isTabInitializing}
                                    >
                                        {t("agent_send")}
                                    </button>
                                </Clickable>
                            )}
                        </div>
                    </div>
                </>
            ) : (
                <div className="agent_pane__empty">
                    <Clickable onClick={() => store.createClaudeSession()}>
                        <button
                            className="agent_pane__empty_btn"
                            disabled={!store.isConnected}
                        >
                            {t("agent_new_session")}
                        </button>
                    </Clickable>
                </div>
            )}
            {store.confirmCloseId && (
                <ConfirmDialog
                    title={t("agent_close_session")}
                    message={t("agent_close_session_confirm")}
                    confirmLabel={t("agent_close_session")}
                    danger
                    onConfirm={() => { store.closeClaudeSession(store.confirmCloseId!); store.changeConfirmCloseId(null); }}
                    onCancel={() => store.changeConfirmCloseId(null)}
                />
            )}
            <SessionSettingsModal store={store} />
        </div>
    );
}, AgentPaneStore);
